import React, { useState } from 'react';
import { Mail, Phone, Shirt, CheckCircle, Send } from 'lucide-react';

const ApparelContact = ({ company = {} }) => {
  const [formState, setFormState] = useState('idle'); // idle | submitting | success

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormState('submitting');
    // Simulate API call
    setTimeout(() => {
      setFormState('success');
    }, 1500);
  };

  return (
    <div className="py-24 px-6 bg-gradient-to-br from-gray-950 via-slate-900 to-black min-h-screen">
      <div className="max-w-6xl mx-auto bg-black/40 rounded-[3rem] overflow-hidden shadow-2xl flex flex-col md:flex-row-reverse border border-white/10 backdrop-blur-2xl">
        
        {/* Contact Info Sidebar */}
        <div className="p-12 md:p-16 lg:p-20 text-white md:w-5/12 relative z-10 flex flex-col justify-center">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none"></div>
          
          <h2 className="text-5xl font-black mb-8 relative z-10 leading-tight">
            Scale Your <br/>
            <span className="text-transparent bg-gradient-to-r from-gray-100 via-gray-400 to-gray-500 bg-clip-text drop-shadow-sm">
              Fashion Line
            </span>
          </h2>
          
          <p className="text-gray-400 mb-12 text-lg leading-relaxed relative z-10">
            From prototype to production, partner with us for sustainable apparel manufacturing. Our facilities handle high-volume global distribution with precision.
          </p>
          
          <div className="space-y-4 relative z-10">
            <div className="flex items-center gap-5 p-6 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/5 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center text-gray-300 group-hover:scale-110 transition-transform">
                <Mail size={22} />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Email Us</p>
                <a href="mailto:ceylonapparelhouse@ddlankagroup.com" className="font-bold text-lg">ceylonapparelhouse@ddlankagroup.com</a>
              </div>
            </div>

            <div className="flex items-center gap-5 p-6 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/5 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center text-gray-300 group-hover:scale-110 transition-transform">
                <Phone size={22} />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Call Directly</p>
                <a href="tel:+94706936939" className="font-bold text-lg">+94 70 693 6939</a>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/5 flex items-center gap-3 text-sm font-semibold text-gray-500">
              <Shirt size={18} className="text-gray-400" />
              <span>Certified Sustainable | Global Scale | 24h Response</span>
            </div>
          </div>
        </div>
        
        {/* Form Section */}
        <div className="p-12 md:p-16 lg:p-20 bg-white/[0.02] md:w-7/12 border-r border-white/5">
          {formState === 'success' ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-6 animate-in zoom-in duration-500">
              <div className="w-24 h-24 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center shadow-2xl shadow-green-500/20">
                <CheckCircle size={48} />
              </div>
              <h3 className="text-3xl font-bold text-white">Inquiry Received</h3>
              <p className="text-gray-400 max-w-xs">Our production managers are reviewing your requirements. We will contact you within 24 hours.</p>
              <button 
                onClick={() => setFormState('idle')}
                className="text-gray-300 underline font-semibold hover:text-white transition-colors"
              >
                Send another inquiry
              </button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Company Name</label>
                  <input 
                    type="text" 
                    placeholder="Brand/Company" 
                    required
                    className="w-full p-5 rounded-2xl border border-white/10 bg-white/5 focus:border-white/30 focus:outline-none transition-all text-white placeholder-gray-600 font-medium" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Email Address</label>
                  <input 
                    type="email" 
                    placeholder="name@company.com" 
                    required
                    className="w-full p-5 rounded-2xl border border-white/10 bg-white/5 focus:border-white/30 focus:outline-none transition-all text-white placeholder-gray-600 font-medium" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Production Volume</label>
                <select className="w-full p-5 rounded-2xl border border-white/10 bg-white/5 focus:border-white/30 focus:outline-none transition-all text-white font-medium appearance-none">
                  <option className="bg-gray-900">100 - 500 units (Sample Batch)</option>
                  <option className="bg-gray-900">500 - 5,000 units (Scale-up)</option>
                  <option className="bg-gray-900">5,000+ units (Bulk Production)</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Requirements</label>
                <textarea 
                  placeholder="Tell us about fabrics, sizing, and your target delivery timeline..." 
                  rows="5"
                  required
                  className="w-full p-5 rounded-2xl border border-white/10 bg-white/5 focus:border-white/30 focus:outline-none transition-all text-white placeholder-gray-600 font-medium resize-none"
                ></textarea>
              </div>

              <button 
                type="submit"
                disabled={formState === 'submitting'}
                className="w-full py-6 bg-white text-black rounded-2xl font-black text-xl hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 group"
              >
                {formState === 'submitting' ? (
                  <div className="w-6 h-6 border-4 border-black/20 border-t-black rounded-full animate-spin"></div>
                ) : (
                  <>
                    Request Production Quote
                    <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Trust Badges / Capabilities */}
      <div className="mt-32 text-center max-w-5xl mx-auto space-y-12">
        <div className="space-y-4">
          <h3 className="text-4xl font-black text-white">Full-Spectrum Manufacturing</h3>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">From high-performance activewear to luxury garments, we bring your vision to life with ethical and sustainable practices.</p>
        </div>
        
        <div className="flex flex-wrap gap-4 justify-center">
          {[
            "👕 Activewear & Performance",
            "🧥 Premium Outerwear",
            "👗 Luxury Garments",
            "♻️ Organic & Recycled Fabrics",
            "📏 Precise Grade Patterning",
            "🌍 Global Logistics"
          ].map((cap, idx) => (
            <span 
              key={idx}
              className="px-6 py-4 bg-white/5 border border-white/10 rounded-full text-gray-300 font-bold hover:bg-white/10 transition-colors"
            >
              {cap}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ApparelContact;